import { destination, lineString } from "@turf/turf"
import { Localization, SettingsManager } from "@viamap/viamap2-common"
import jsPDF from "jspdf"
import { message } from "src/components/Message"
import { formatMatrikelEjerlavLine } from "src/components/MatrikelRapportData"
import { PDFArray, PDFDict, PDFDocument, PDFImage, PDFName, PDFNumber, PDFString } from "pdf-lib"
import { ImageSource } from "maplibre-gl"
import { lengthByUserPreference } from "src/managers/PlanarCalculationMethods"

export function drawFoldMarkers(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  drawingOptions: { fold, format, foldFormat, margin }
) {
  if (drawingOptions.fold !== true) {
    return
  }

  const fSize = Number(drawingOptions.format.split("A")[1][0])
  const fSizeA0 = Number(drawingOptions.format.split("A")[0] || 1)

  const ffSize = Number(drawingOptions.foldFormat.split("A")[1][0])
  const ffSizeA0 = Number(drawingOptions.foldFormat.split("A")[0] || 1)
  const distance = (ffSize - Math.log2(ffSizeA0)) - (fSize - Math.log2(fSizeA0))
  if (distance < 1) {
    return null
  }

  const direction = canvas.height < canvas.width

  const lxfolds = Math.floor(distance / 2) + (distance % 2)
  const sxfolds = Math.floor(distance / 2)

  const lxTfolds = 2 ** lxfolds;
  const sxTfolds = 2 ** sxfolds;


  // ctx.save()
  // ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.fillStyle = "#000"

  let xSize = canvas.width / ((direction && lxTfolds) || sxTfolds);
  let ySize = canvas.height / ((direction && sxTfolds) || lxTfolds);

  ctx.save()

  for (let i = 0; i < 2; i++) {
    for (let x = 0; x <= lxTfolds; x++) {
      const xcpos = xSize * x;
      ctx.fillRect(xcpos - 1, drawingOptions.margin * 4, 2, drawingOptions.margin * 4 + 25)
    }

    for (let y = 0; y <= lxTfolds; y++) {
      const ycpos = ySize * y;
      ctx.fillRect(drawingOptions.margin * 4, ycpos - 1, drawingOptions.margin * 4 + 25, 2)
    }

    ctx.translate(canvas.width, canvas.height);
    ctx.rotate(Math.PI);
  }
  ctx.restore()
}

export async function canvasPDFLIB(canvas: HTMLCanvasElement | string, canvasSource: ImageSource, paperFormat: string, compression: string | number) {
  let canvasElem = typeof canvas == "string" ? document.getElementById(canvas) : canvas
  const doc = await PDFDocument.create();
  let page = doc.addPage([paperToSize(paperFormat).width * 2.83465, paperToSize(paperFormat).height * 2.83465]);
  let pdfImage: PDFImage
  if (typeof compression === "number") {
    pdfImage = await doc.embedJpg(canvasElem instanceof HTMLCanvasElement ? canvasElem.toDataURL("image/jpeg", compression) : "");
  } else {
    pdfImage = await doc.embedPng(canvasElem instanceof HTMLCanvasElement ? canvasElem.toDataURL("image/png") : "");
  }
  page.drawImage(pdfImage, {
    x: 0,
    y: 0,
    width: paperToSize(paperFormat).width * 2.83465,
    height: paperToSize(paperFormat).height * 2.83465,
  });
  let vpArray = PDFArray.withContext(doc.context)
  let vpArrDict = PDFDict.withContext(doc.context)
  let measureDict = PDFDict.withContext(doc.context)
  let bbox = PDFArray.withContext(doc.context)
  bbox.push(PDFNumber.of(0))
  bbox.push(PDFNumber.of(0))
  bbox.push(PDFNumber.of(paperToSize(paperFormat).width * 2.83465))
  bbox.push(PDFNumber.of(paperToSize(paperFormat).height * 2.83465))
  let bounds = PDFArray.withContext(doc.context)
  bounds.push(PDFNumber.of(0))
  bounds.push(PDFNumber.of(0))
  bounds.push(PDFNumber.of(1))
  bounds.push(PDFNumber.of(0))
  bounds.push(PDFNumber.of(1))
  bounds.push(PDFNumber.of(1))
  bounds.push(PDFNumber.of(0))
  bounds.push(PDFNumber.of(1))
  let GCS = PDFDict.withContext(doc.context)
  GCS.set(PDFName.of("EPSG"), PDFNumber.of(4093))
  GCS.set(PDFName.of("Type"), PDFName.of("PROJCS"))
  GCS.set(PDFName.of("WKT"), PDFString.of(`PROJCS["DKTM1",GEOGCS["EPSG:4258",DATUM["EPSG:6258",SPHEROID["EPSG:7019",6378137.000,298.25722210],TOWGS84[0.0000,0.0000,0.0000,0.000000,0.000000,0.000000,0.00000000]],PRIMEM["Greenwich",0],UNIT["Degree",0.017453292519943295]],PROJECTION["Transverse_Mercator"],PARAMETER["False_Easting",200000.000],PARAMETER["False_Northing",-5000000.000],PARAMETER["Scale_Factor",0.999980000000],PARAMETER["Central_Meridian",9.00000000000000],PARAMETER["Latitude_Of_Origin",0.00000000000000],UNIT["Meter",1.00000000000000]]`))
  let GPTS = PDFArray.withContext(doc.context)
  canvasSource.coordinates.toReversed().forEach(([x, y]) => {
    GPTS.push(PDFNumber.of(y))
    GPTS.push(PDFNumber.of(x))
  });
  let LPTS = PDFArray.withContext(doc.context)
  LPTS.push(PDFNumber.of(0))
  LPTS.push(PDFNumber.of(0))
  LPTS.push(PDFNumber.of(1))
  LPTS.push(PDFNumber.of(0))
  LPTS.push(PDFNumber.of(1))
  LPTS.push(PDFNumber.of(1))
  LPTS.push(PDFNumber.of(0))
  LPTS.push(PDFNumber.of(1))
  let PDU = PDFArray.withContext(doc.context)
  PDU.push(PDFString.of("M"))
  PDU.push(PDFString.of("SQM"))
  PDU.push(PDFString.of("DEG"))
  measureDict.set(PDFName.of("Bounds"), bounds);
  measureDict.set(PDFName.of("GCS"), GCS);
  measureDict.set(PDFName.of("GPTS"), GPTS);
  measureDict.set(PDFName.of("LPTS"), LPTS);
  measureDict.set(PDFName.of("PDU"), PDU);
  measureDict.set(PDFName.of("Subtype"), PDFName.of("GEO"));
  measureDict.set(PDFName.of("Type"), PDFName.of("Measure"));
  vpArrDict.set(PDFName.of("BBox"), bbox);
  vpArrDict.set(PDFName.of("Measure"), measureDict);
  vpArrDict.set(PDFName.of("Type"), PDFName.of("Viewport"));
  vpArray.push(vpArrDict)
  page.node.set(PDFName.of("VP"), vpArray);
  console.log("page", page)
  const pdfBytes = await doc.save();
  const blob = new Blob([pdfBytes as any], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  console.log("url", url)
  return url;
}

/**
 * @deprecated Use canvasPDFLIB instead
 */
export function canvasToPDF(canvas: HTMLCanvasElement | string, paperFormat: string, compression: string | number) {
  let canvasElem = typeof canvas == "string" ? document.getElementById(canvas) : canvas

  let paper = ("" + paperFormat).toLowerCase()

  const doc = new jsPDF({
    format: paper.slice(0, - 1),
    orientation: paper.at(-1) as any
  });

  let paperS = paperToSize(paperFormat)
  if (canvasElem instanceof HTMLCanvasElement) {
    let img: string = ""
    if (compression === "PNG")
      img = canvasElem.toDataURL("image/png");
    if (typeof compression === "number")
      img = canvasElem.toDataURL("image/jpeg", compression);
    doc.addImage(img, 0, 0, paperS.width, paperS.height)
    doc.save(saveWithTimeStampt("mapit-Plot") + ".pdf");
  } else {
    message.error("Coundn't save to PDF")
  }
}

export function saveWithTimeStampt(name: string) {
  let now = new Date();

  let yyyy = numberFixedWidth(now.getFullYear(), 4);
  let MM = numberFixedWidth(now.getMonth() + 1);
  let dd = numberFixedWidth(now.getDate());
  let hh = numberFixedWidth(now.getHours());
  let mm = numberFixedWidth(now.getMinutes());
  let ss = numberFixedWidth(now.getSeconds());

  return yyyy + MM + dd + "_" + name + "_" + hh + mm + ss;
}

function numberFixedWidth(number: number, width: number = 2) {
  let padding = ("0".repeat(width))
  return (padding + number).slice(-width)
}

export function paperToSize(paper: string): { height: number, width: number } {
  const PaperSizes = {
    "4A0L": { height: 1682, width: 2378 },
    "4A0P": { width: 1682, height: 2378 },
    "2A0L": { height: 1189, width: 1682 },
    "2A0P": { width: 1189, height: 1682 },
    "A0L": { height: 841, width: 1189 },
    "A0P": { width: 841, height: 1189 },
    "A1L": { height: 594, width: 841 },
    "A1P": { width: 594, height: 841 },
    "A2L": { height: 420, width: 594 },
    "A2P": { width: 420, height: 594 },
    "A3L": { height: 297, width: 420 },
    "A3P": { width: 297, height: 420 },
    "A4L": { height: 210, width: 297 },
    "A4P": { width: 210, height: 297 },
    "A5L": { height: 148, width: 210 },
    "A5P": { width: 148, height: 210 },
    "A6L": { height: 105, width: 148 },
    "A6P": { width: 105, height: 148 },
    "A7L": { height: 74, width: 105 },
    "A7P": { width: 74, height: 105 },
    "A8L": { height: 52, width: 74 },
    "A8P": { width: 52, height: 74 },
    "A9L": { height: 37, width: 52 },
    "A9P": { width: 37, height: 52 },
    "A10L": { height: 26, width: 37 },
    "A10P": { width: 26, height: 37 },
  }
  return PaperSizes[paper] || PaperSizes["A4P"]
}

// MARK: Paper Projections
export function PlotLayerCornerArr(center, rot, scale, paperFormat) {
  const RadToDeg = 57.2957795
  const sideLength = paperToSize(paperFormat)

  let angle = RadToDeg * Math.atan(sideLength.width / sideLength.height)
  let hypo = Math.sqrt(sideLength.width ** 2 + sideLength.height ** 2)

  let rotation = rot;
  function calcProjectedSideLength(c, scale, rot) {
    return [
      destination(c, (hypo / 2) * scale / 1_000_000, rot - angle).geometry.coordinates[0] - c[0],
      destination(c, (hypo / 2) * scale / 1_000_000, rot - angle).geometry.coordinates[1] - c[1],
    ]
  }

  function transpose(c, sl) {
    return [
      c[0] + sl[0], c[1] + sl[1]
    ]
  }

  return [
    transpose(center, calcProjectedSideLength(center, scale, rotation) as [number, number]),
    transpose(center, calcProjectedSideLength(center, scale, rotation + (2 * angle)) as [number, number]),
    transpose(center, calcProjectedSideLength(center, scale, rotation + 180) as [number, number]),
    transpose(center, calcProjectedSideLength(center, scale, rotation + 180 + (2 * angle)) as [number, number]),
    transpose(center, calcProjectedSideLength(center, scale, rotation) as [number, number]),
  ]
}

export function maxWebGLSize(ctx: WebGL2RenderingContext | WebGLRenderingContext | null): number {
  if (!ctx) {
    return 4096 // Default max texture size
  }
  let max = ctx.getParameter(ctx.MAX_TEXTURE_SIZE) || 4096 as number
  return max
}


// MARK: Compose drawing
export async function drawDrawing(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  legendCanvas: HTMLCanvasElement,
  map: HTMLCanvasElement | HTMLImageElement,
  drawingOptions: { center, bearing, scale, format, margin, arrowImg, title, description, imgCache, legendIgnoreBlocks, showLegend, attrib, logoImg: Promise<null | HTMLCanvasElement>, moelbakLogoImg: Promise<null | HTMLCanvasElement>, includeDate, modus?: string, ejendomskortData?: any, moelbakJournalNumber?: string, licensedSurveyorName?: string, ejendomskortPurposeText?: string }
) {
  if (drawingOptions.modus === "Ejendomskort") {
    await drawEjendomskortDrawing(ctx, canvas, map, drawingOptions);
    return;
  }

  const Corners = PlotLayerCornerArr(drawingOptions.center, drawingOptions.bearing, drawingOptions.scale, drawingOptions.format)
  const CW = canvas.width;
  const CH = canvas.height;
  ctx.clearRect(0, 0, CW, CH);
  ctx.drawImage(map, 0, 0);
  ctx.fillStyle = "white";

  const mm = 8 // ?

  const Margin = drawingOptions.margin * mm
  ctx.fillRect(0, 0, CW, Margin);
  ctx.fillRect(0, 0, Margin, CH);
  ctx.fillRect(0, CH - Margin, CW, Margin);
  ctx.fillRect(CW - Margin, 0, Margin, CH);

  let Bottom = Corners.map((a) => a).splice(2, 2)
  const BottomLengthKM = lengthByUserPreference(lineString(Bottom));
  const estW = BottomLengthKM * 1000 * 0.3;
  let restW = estW % 100;
  if (estW < 100) {
    restW = estW % 20;
  }
  if (estW < 20) {
    restW = estW % 5;
  }

  const optimalW = estW - restW;
  const ScalaToOptimal = optimalW / estW;


  ctx.strokeStyle = "#000"
  ctx.lineWidth = 1;
  ctx.beginPath()
  ctx.moveTo(Margin, Margin)
  ctx.lineTo(CW - Margin, Margin)
  ctx.lineTo(CW - Margin, CH - Margin)
  ctx.lineTo(Margin + CW * 0.3 * ScalaToOptimal + 100, CH - Margin);
  ctx.lineTo(Margin + CW * 0.3 * ScalaToOptimal + 100, CH - Margin - 60);
  ctx.lineTo(Margin, CH - Margin - 60);
  ctx.lineTo(Margin, CH - Margin)
  ctx.lineTo(Margin, Margin)
  ctx.lineTo(Margin + 10, Margin)
  ctx.stroke()



  const ImgNArrow = await drawingOptions.arrowImg
  if (ImgNArrow) {
    const ImgNArrowScale = 1 / 3
    const IW = ImgNArrow.width * ImgNArrowScale;
    const IH = ImgNArrow.height * ImgNArrowScale;
    const IBig = IW > IH ? IW : IH;
    const IOFF = Margin + 20 + (IBig / 2)
    ctx.save()
    ctx.translate(IOFF, IOFF)
    ctx.rotate(((-(drawingOptions.bearing + 360) % 360) * Math.PI / 180))
    ctx.drawImage(ImgNArrow, -IW / 2, -IH / 2, IW, IH)
    ctx.restore()
  }


  let totalImgHeight = 0;
  let sigWidth = (210 * mm) * 3 / 10;
  ctx.font = "45px Arial";
  let titleWrap = wrapText(ctx, drawingOptions.title.trim(), sigWidth)
  ctx.font = "30px Arial";
  let date = drawingOptions.includeDate ? " \n " + new Date().toLocaleDateString(Localization.getLocale(), { year: "numeric", month: "2-digit", day: "2-digit" }) : "";
  let lines = wrapText(ctx, (ParseNewlinesAndSpaces(drawingOptions.description).trim() + date).trim(), sigWidth)

  titleWrap.forEach(async (a: string) => {
    let img = drawingOptions.imgCache?.[a || "a"] || null;
    if (img) {
      let awaited = (await img)
      if (awaited) {
        totalImgHeight += (awaited.height * sigWidth / awaited.width) - 45;
      }
    }
  })
  lines.forEach(async (a: string) => {
    let img = drawingOptions.imgCache?.[a || "a"] || null;
    if (img) {
      let awaited = (await img)
      if (awaited) {
        totalImgHeight += (awaited.height * sigWidth / awaited.width) - 30;
      }
    }
  })



  const IgnBlock = drawingOptions.legendIgnoreBlocks
  let dIgnBlock = [...IgnBlock, { start: legendCanvas.height, end: legendCanvas.height }]
  let legendHeight = (drawingOptions.showLegend && dIgnBlock.reduce((a, b) => a - (b.end - b.start), legendCanvas.height)) || 0;
  let box = titleWrap.length * 45 + lines.length * 30 + 15 + totalImgHeight;

  // Attrib
  ctx.strokeStyle = "#fff";
  ctx.fillStyle = "#000";
  ctx.font = "18px Arial";
  let attrib = drawingOptions.attrib.filter((a) => a).map((a) => "© " + a);
  attrib.sort((a, b) => { return ctx.measureText(b).width - ctx.measureText(a).width });
  attrib.forEach((str, idx) => {
    let strW = ctx.measureText(str).width
    ctx.strokeText(str, (CW - Margin - 7) - strW, CH - (Margin + legendHeight + 22 * 8 + idx * 19));
    ctx.fillText(str, (CW - Margin - 7) - strW, CH - (Margin + legendHeight + 22 * 8 + idx * 19));
  })

  ctx.strokeStyle = "#000";
  ctx.fillStyle = "#fff"
  ctx.fillRect(CW - ((sigWidth) + Margin + 30), CH - (Margin + legendHeight + 30 + box + 15), sigWidth + 10 + 20 + 15, legendHeight + 20 + box + 15 + 15)
  ctx.beginPath()
  ctx.moveTo(CW - Margin, CH - (Margin + legendHeight + 30 + box + 20))
  ctx.lineTo(CW - Margin, CH - (Margin + legendHeight + 30 + box + 15))
  ctx.lineTo(CW - ((sigWidth) + Margin + 30), CH - (Margin + legendHeight + 30 + box + 15))
  ctx.lineTo(CW - ((sigWidth) + Margin + 30), CH - Margin)
  ctx.stroke()

  let currTTop = CH - (2 * Margin + legendHeight + 20 + box + 15);
  ctx.fillStyle = "#000"
  ctx.font = "45px Arial";
  for (let i = 0; i < titleWrap.length; i++) {
    let text = titleWrap[i];
    let img = drawingOptions.imgCache?.[text || "a"] || null;
    if (img) {
      let awaited = (await img)
      if (awaited) {
        ctx.drawImage(awaited, CW - ((sigWidth) + Margin + 10), Margin + currTTop, sigWidth, awaited.height * sigWidth / awaited.width)
        currTTop += (awaited.height * sigWidth / awaited.width);
      }
    } else {
      ctx.fillText(text, CW - ((sigWidth) + Margin + 10), 35 + Margin + currTTop)
      currTTop += 45
    }
  }
  ctx.font = "24px Arial";
  for (let i = 0; i < lines.length; i++) {
    let text = lines[i];
    let img = drawingOptions.imgCache?.[text || "a"] || null;
    if (img) {
      let awaited = (await img)
      if (awaited) {
        ctx.drawImage(awaited, CW - ((sigWidth) + Margin + 10), Margin + currTTop, sigWidth, awaited.height * sigWidth / awaited.width)
        currTTop += (awaited.height * sigWidth / awaited.width);
      }
    } else {
      ctx.fillText(text, CW - ((sigWidth) + Margin + 10), 35 + Margin + currTTop)
      currTTop += 30
    }
  }

  let legendBlockPadding = 20
  let legendInlinePadding = 20

  let currTop = 0 + legendBlockPadding + currTTop + 20;
  let lIP = legendInlinePadding
  let legendWidth = sigWidth // awaitedImages.reduce((a,b) => b !== "" ? (b.width > a ? b.width : a) : a , 0) * legnedScaling
  if (legendHeight) {
    ctx.fillStyle = "#fff";
    ctx.fillRect(CW - Margin - legendWidth - lIP, Margin + currTop - legendBlockPadding, legendWidth + lIP, legendHeight + legendBlockPadding * 2)

    let lOff = 0;
    let inOff = 0;
    const IgnBlock = drawingOptions.legendIgnoreBlocks
    let dIgnBlock = [...IgnBlock, { start: legendCanvas.height, end: legendCanvas.height }]

    dIgnBlock.forEach((a) => {
      ctx.drawImage(legendCanvas,
        0, inOff, legendCanvas.width, a.start - inOff,
        CW - Margin - legendWidth - lIP / 2, Margin + currTop + lOff, legendCanvas.width, a.start - inOff)
      lOff += a.start - inOff;
      inOff = a.end;
    });

  }


  const ImgLogo = await drawingOptions.logoImg;
  if (ImgLogo) {
    const desireHeight = SettingsManager.getSystemSetting("plot.image.desiredHeight", 150);
    const IScale = desireHeight / ImgLogo.height;
    const IW = ImgLogo.width * IScale;
    const IH = ImgLogo.height * IScale;
    const IOFFW = Margin + 30 + sigWidth + SettingsManager.getSystemSetting("plot.image.offsetX", 0);
    const IOFFH = Margin + SettingsManager.getSystemSetting("plot.image.offsetY", 0);

    ctx.save()
    ctx.translate(CW - (IOFFW), CH - IOFFH)
    // ctx.beginPath();
    // ctx.arc(0, 0, 10, 0, 2 * Math.PI);
    // ctx.stroke();

    ctx.drawImage(ImgLogo, 0, 0, -IW, -IH)
    ctx.restore()
  }




  // Ruler
  // let Bottom = Corners.map((a) => a).splice(2, 2)
  // const BottomLengthKM = Turf.length(Turf.lineString(Bottom));

  ctx.font = "30px Arial";

  // const estW = BottomLengthKM*1000*0.3;
  // let restW = estW % 100;
  // if (estW < 100) {
  //   restW = estW % 20;
  // }
  // if (estW < 20) {
  //   restW = estW % 5;
  // }

  // const optimalW = estW - restW;
  // const ScalaToOptimal = optimalW/estW;




  const visualW = Math.round(optimalW);
  ctx.beginPath();
  // ctx.moveTo(Margin-5, CH-Margin-100);
  // ctx.lineTo(Margin + CW * 0.3 * ScalaToOptimal / 2 + 5, CH-Margin-25 - 10);
  // ctx.lineTo(Margin + CW * 0.3 * ScalaToOptimal + 5, CH-Margin-10 - 10);
  ctx.lineTo(Margin - 5, CH - Margin - 60);
  ctx.lineTo(Margin + CW * 0.3 * ScalaToOptimal + 100, CH - Margin - 60);
  ctx.lineTo(Margin + CW * 0.3 * ScalaToOptimal + 100, CH - Margin + 5);
  ctx.lineTo(Margin - 5, CH - Margin + 5);
  ctx.fillStyle = "#fff";
  ctx.fill();

  ctx.fillStyle = "#ff5722";
  ctx.strokeStyle = 'white';
  ctx.lineWidth = 5;
  // ctx.fillRect(0, CH-10, CW / 2, 10);
  ctx.fillRect(Margin, CH - Margin - 10, CW * 0.3 * ScalaToOptimal, 10);
  ctx.fillRect(Margin + CW * 0.3 * ScalaToOptimal, CH - Margin - 10, -5, -5)
  ctx.lineTo(Margin + CW * 0.3 * ScalaToOptimal, CH - Margin - 10)
  ctx.fillRect(Margin, CH - Margin - 15, 5, 12);
  ctx.fillStyle = "#000";
  ctx.strokeText(visualW + " m", Margin + CW * 0.3 * ScalaToOptimal + 7, CH - Margin - 5);
  ctx.fillText(visualW + " m", Margin + CW * 0.3 * ScalaToOptimal + 7, CH - Margin - 5);
  ctx.fillRect(Margin, CH - Margin - 25, CW * 0.3 * ScalaToOptimal / 2, 10);
  ctx.fillRect(Margin + CW * 0.3 * ScalaToOptimal / 2, CH - Margin - 25, -5, -5);
  ctx.fillRect(Margin, CH - Margin - 30, 5, 15);
  ctx.strokeText(visualW / 2 + " m", Margin + (CW * 0.3 * ScalaToOptimal) / 2 + 7, CH - Margin - 17);
  ctx.fillText(visualW / 2 + " m", Margin + (CW * 0.3 * ScalaToOptimal) / 2 + 7, CH - Margin - 17);

  let paper = (drawingOptions.format as any)
  paper = paper.slice(0, -1);


  ctx.fillStyle = "#000"
  ctx.strokeText("1:" + drawingOptions.scale + " (" + (paper) + ")", Margin + 7, CH - Margin - 34);
  ctx.fillText("1:" + drawingOptions.scale + " (" + (paper) + ")", Margin + 7, CH - Margin - 34);
  ctx.fillStyle = "#ff5722";
  ctx.fillText("1:", Margin + 7, CH - Margin - 34);
  ctx.fillStyle = "#000";
  ctx.fillText("1", Margin + 7, CH - Margin - 34);

  ctx.fillStyle = "#ff5722";

  ctx.fillStyle = "#000";
}

async function drawEjendomskortDrawing(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  map: HTMLCanvasElement | HTMLImageElement,
  drawingOptions: { scale, format, margin, bearing?: number, arrowImg?: Promise<null | HTMLImageElement>, moelbakLogoImg?: Promise<null | HTMLCanvasElement>, ejendomskortData?: any, moelbakJournalNumber?: string, licensedSurveyorName?: string, ejendomskortPurposeText?: string }
) {
  const CW = canvas.width;
  const CH = canvas.height;
  const mm = 8;
  const Margin = drawingOptions.margin * mm;
  const data = drawingOptions.ejendomskortData;
  const locale = "da-DK";
  const today = new Date();
  const pad = 18;
  const boxBottomPad = 8;
  const footerTopGap = 24;
  const minBoxWidth = (210 * mm) * 0.48;
  const maxBoxWidth = Math.min(CW - Margin * 2 - 20, (210 * mm) * 0.62);
  const formatSquareMeters = (value: number) => Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value || 0) + " m²";
  const monthYear = today.toLocaleDateString("da-DK", { month: "2-digit", year: "numeric" });
  const printDate = today.toLocaleDateString("da-DK", { day: "2-digit", month: "long", year: "numeric" });
  const paper = (drawingOptions.format as string).slice(0, -1);
  const lineSpacing = (fontSize: number) => Math.round(fontSize * 1.45);
  const logoHeight = 46;
  const logoToContactGap = 8;
  const headerContactFontSize = 12;
  const headerContactLineHeight = 14;
  const headerToContentGap = 22;
  const firstContentFontSize = 17;
  const headerHeight =
    logoHeight +
    logoToContactGap +
    headerContactLineHeight * 2 +
    headerToContentGap +
    Math.ceil(firstContentFontSize * 0.85);

  type MeasuredBlock = { lines: string[], font: string, fontSize: number, lineHeight: number, color?: string };
  const measureBlock = (text: string, font: string, fontSize: number, maxWidth: number, color?: string): MeasuredBlock => {
    ctx.font = font;
    const lineHeight = lineSpacing(fontSize);
    return { lines: wrapText(ctx, text, maxWidth), font, fontSize, lineHeight, color };
  };
  const blockHeight = (block: MeasuredBlock) => block.lines.length * block.lineHeight;

  const withinGroupGap = 5;
  const betweenGroupGap = 24;
  const footerWithinGap = 5;

  type ContentSpec = { text: string, font: string, fontSize: number, color?: string };
  let contentGroups: ContentSpec[][] = [];
  let footerLeftSpecs: ContentSpec[] = [];
  let footerRightSpecs: ContentSpec[] = [];

  if (data) {
    const hasMultipleProperties = (data.propertyCount || 0) > 1;
    const propertyLabel = hasMultipleProperties ? "Ejendomme" : "Ejendom";
    const areaLabel = hasMultipleProperties ? "Ejendommenes samlede areal udgør:" : "Ejendommens samlede areal udgør:";
    const purposeText = (drawingOptions.ejendomskortPurposeText || "").trim();
    const preparedPurposeText = purposeText ? ` ${purposeText}` : "";

    contentGroups = [
      [
        { text: `Matr.nr.: ${formatMatrikelEjerlavLine(data.matrikelEjerlavGroups, "Ukendt matrikel")}`, font: "bold 17px Arial", fontSize: 17 },
        { text: formatKommune(data.kommune), font: "16px Arial", fontSize: 16 },
        { text: data.region || "Ukendt region", font: "16px Arial", fontSize: 16 },
      ],
      [
        { text: `${propertyLabel}: ${data.sfeNumre.join(", ")}`, font: "16px Arial", fontSize: 16 },
        { text: `${areaLabel} ${formatSquareMeters(data.samletAreal)}, heraf vej ${formatSquareMeters(data.vejareal)}`, font: "16px Arial", fontSize: 16 },
      ],
      [
        { text: "Kortet er i overensstemmelse med registreringen i matriklen.", font: "16px Arial", fontSize: 16 },
        { text: `Udfærdiget i ${monthYear}${preparedPurposeText}`, font: "16px Arial", fontSize: 16 },
      ],
    ];
    footerLeftSpecs = [
      { text: `Målforhold: 1:${Number(drawingOptions.scale).toLocaleString("da-DK")} (${paper})`, font: "15px Arial", fontSize: 15 },
      { text: `Landinspektør j.nr.: ${drawingOptions.moelbakJournalNumber || ""}`, font: "15px Arial", fontSize: 15 },
    ];
    footerRightSpecs = [
      { text: drawingOptions.licensedSurveyorName || "", font: "italic bold 16px Arial", fontSize: 16 },
      { text: "beskikket landinspektør", font: "italic bold 15px Arial", fontSize: 15 },
      { text: `Dato: ${printDate}`, font: "italic bold 15px Arial", fontSize: 15 },
    ];
  } else {
    contentGroups = [[{ text: "Vælg ejendomslag for at udfylde tegningshovedet.", font: "16px Arial", fontSize: 16 }]];
  }

  const measureGroup = (specs: ContentSpec[], maxWidth: number) =>
    specs.map((spec) => measureBlock(spec.text, spec.font, spec.fontSize, maxWidth, spec.color));

  const groupHeight = (blocks: MeasuredBlock[]) =>
    blocks.reduce((sum, block, idx) => sum + blockHeight(block) + (idx ? withinGroupGap : 0), 0);

  let boxWidth = minBoxWidth;
  let contentBlockGroups: MeasuredBlock[][] = [];
  let footerLeftBlocks: MeasuredBlock[] = [];
  let footerRightBlocks: MeasuredBlock[] = [];

  for (let attempt = 0; attempt < 5; attempt++) {
    const innerWidth = boxWidth - pad * 2;
    const leftColWidth = innerWidth * 0.56;
    const rightColWidth = innerWidth * 0.40;
    contentBlockGroups = contentGroups.map((group) => measureGroup(group, innerWidth));
    footerLeftBlocks = footerLeftSpecs.map((spec) => measureBlock(spec.text, spec.font, spec.fontSize, leftColWidth, spec.color));
    footerRightBlocks = footerRightSpecs.map((spec) => measureBlock(spec.text, spec.font, spec.fontSize, rightColWidth, spec.color));

    const matrLines = contentBlockGroups[0]?.[0]?.lines.length || 1;
    if (matrLines <= 10 || boxWidth >= maxBoxWidth || attempt === 4) {
      break;
    }
    boxWidth = Math.min(maxBoxWidth, boxWidth + (210 * mm) * 0.05);
  }

  const innerWidth = boxWidth - pad * 2;
  const leftColWidth = innerWidth * 0.56;
  const rightColWidth = innerWidth * 0.40;

  const contentHeight = contentBlockGroups.reduce((sum, group, groupIdx) =>
    sum + groupHeight(group) + (groupIdx ? betweenGroupGap : 0), 0);
  const footerHeight = Math.max(
    footerLeftBlocks.reduce((sum, block, idx) => sum + blockHeight(block) + (idx ? footerWithinGap : 0), 0),
    footerRightBlocks.reduce((sum, block, idx) => sum + blockHeight(block) + (idx ? footerWithinGap : 0), 0),
  );
  const boxHeight = pad + headerHeight + contentHeight + (footerHeight ? footerTopGap + footerHeight : 0) + boxBottomPad;
  const boxX = CW - Margin - boxWidth;
  const boxY = CH - Margin - boxHeight;

  ctx.clearRect(0, 0, CW, CH);
  ctx.drawImage(map, 0, 0);

  ctx.fillStyle = "white";
  ctx.fillRect(0, 0, CW, Margin);
  ctx.fillRect(0, 0, Margin, CH);
  ctx.fillRect(0, CH - Margin, CW, Margin);
  ctx.fillRect(CW - Margin, 0, Margin, CH);

  const ImgNArrow = drawingOptions.arrowImg ? await drawingOptions.arrowImg : null;
  if (ImgNArrow && drawingOptions.bearing != null) {
    const ImgNArrowScale = 1 / 3;
    const IW = ImgNArrow.width * ImgNArrowScale;
    const IH = ImgNArrow.height * ImgNArrowScale;
    const IBig = IW > IH ? IW : IH;
    const IOFF = Margin + 20 + (IBig / 2);
    ctx.save();
    ctx.translate(IOFF, IOFF);
    ctx.rotate(((-(drawingOptions.bearing + 360) % 360) * Math.PI / 180));
    ctx.drawImage(ImgNArrow, -IW / 2, -IH / 2, IW, IH);
    ctx.restore();
  }

  ctx.fillStyle = "#fff";
  ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 1;
  ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

  const textX = boxX + pad;
  const headerTopY = boxY + pad;

  const moelbakLogo = drawingOptions.moelbakLogoImg ? await drawingOptions.moelbakLogoImg : null;
  if (moelbakLogo) {
    const logoScale = logoHeight / moelbakLogo.height;
    const logoWidth = moelbakLogo.width * logoScale;
    ctx.drawImage(moelbakLogo, textX, headerTopY, logoWidth, logoHeight);
  }

  ctx.fillStyle = "#000";
  ctx.font = `${headerContactFontSize}px Arial`;
  const contactTopY = headerTopY + logoHeight + logoToContactGap;
  ctx.fillText("Tlf: 70 20 88 83 - molbak@molbak.dk", textX + 2, contactTopY + headerContactLineHeight);
  ctx.fillText("www.molbak.dk", textX + 2, contactTopY + headerContactLineHeight * 2);

  let y = boxY + pad + headerHeight;

  const drawMeasuredBlock = (block: MeasuredBlock, x: number, startY: number) => {
    ctx.font = block.font;
    ctx.fillStyle = block.color || "#000";
    block.lines.forEach((line, idx) => {
      ctx.fillText(line, x, startY + idx * block.lineHeight);
    });
    return startY + blockHeight(block);
  };

  contentBlockGroups.forEach((group, groupIdx) => {
    group.forEach((block, idx) => {
      y = drawMeasuredBlock(block, textX, y) + (idx < group.length - 1 ? withinGroupGap : 0);
    });
    if (groupIdx < contentBlockGroups.length - 1) {
      y += betweenGroupGap;
    }
  });

  if (footerHeight) {
    y += footerTopGap;
    const leftX = textX;
    const rightX = textX + leftColWidth + (innerWidth - leftColWidth - rightColWidth);
    let footerLeftY = y;
    let footerRightY = y;
    footerLeftBlocks.forEach((block, idx) => {
      footerLeftY = drawMeasuredBlock(block, leftX, footerLeftY) + (idx < footerLeftBlocks.length - 1 ? footerWithinGap : 0);
    });
    footerRightBlocks.forEach((block, idx) => {
      footerRightY = drawMeasuredBlock(block, rightX, footerRightY) + (idx < footerRightBlocks.length - 1 ? footerWithinGap : 0);
    });
  }
}

function formatKommune(kommune: string) {
  if (!kommune) {
    return "Ukendt kommune";
  }
  return kommune.toLocaleLowerCase("da-DK").includes("kommune") ? kommune : `${kommune} Kommune`;
}

export async function PromiseCanvas(url: URL | string): Promise<HTMLCanvasElement | null> {
  if (!url) return null
  let img = await PromiseImage(url);
  if (!img) return null
  let x = document.createElement("canvas");
  x.width = img.width;
  x.height = img.height;
  let ctx = x.getContext("2d");
  if (!ctx) return null
  ctx.drawImage(img, 0, 0);
  return x
}

export async function PromiseImage(url: URL | string) {
  return new Promise<HTMLImageElement | "">((resolve) => {
    const img = new Image();
    if (url === "") {
      resolve("")
      return
    }
    img.src = url as unknown as string //Cause Url is a string
    img.crossOrigin = "anonymous"
    img.onload = function () {
      resolve(img)
    }
    img.onerror = async function () {
      const corsProxy = SettingsManager.getSystemSetting("ImageCorsProxyUrl");
      const imgProx = new Image();
      let x = await fetch(corsProxy + `https://${(url as string).split("://")[1]}` as unknown as string, {
        headers: {
          accept: "image/png"
        }
      })
      let blobUrl = URL.createObjectURL(await x.blob())
      imgProx.src = blobUrl //Cause Url is a string
      imgProx.crossOrigin = "anonymous";
      imgProx.onload = function () {
        resolve(imgProx)
      }
      imgProx.onerror = function () {
        resolve("")
      }

    }
  })
}

export function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number) {
  const spaceWidth = ctx.measureText(" ").width
  const words = text.split(" ").map((a) => {
    const newLine = (a !== "\n")
    return newLine ? {
      word: a,
      width: ctx.measureText(a).width,
      newLine: false
    } : { word: "", width: 0, newLine: true }
  })

  let lines: string[] = [];
  let currentLine: string = "";
  let currentLineWidth = 0
  words.forEach((a) => {
    if (a.newLine || currentLineWidth + spaceWidth + a.width > maxWidth) {
      lines.push(currentLine)
      currentLine = a.word
      currentLineWidth = a.width
    } else {
      currentLine += currentLine !== "" ? " " + a.word : a.word;
      currentLineWidth += a.width + spaceWidth
    }
  })
  if (currentLine) {
    lines.push(currentLine)
  }
  return lines
}


function ParseNewlinesAndSpaces(text: string) {
  return text.replaceAll(/[ ]*[\n]/g, " \n ")
}